import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import BagDeliveroo from '@deliveroo/icon-set/react/BagDeliveroo';
import Pin from '@deliveroo/icon-set/react/Pin';
import QuestionCircle from '@deliveroo/icon-set/react/QuestionCircle';
import Icon from '@deliveroo/consumer-component-library/components/Icon';
import P from '@deliveroo/consumer-component-library/components/P';
import Span from '@deliveroo/consumer-component-library/components/Span';
import Button from '@deliveroo/consumer-component-library/components/Button';
import Row from '@deliveroo/consumer-component-library/components/Row';
import UnstyledButton from '@deliveroo/consumer-component-library/components/UnstyledButton';
import InfoBannerContainer from '../../containers/InfoBanner';
import PunchCardContainer from '../../containers/PunchCard';
import OrderItemsSummary from '../OrderItemsSummary';
import OrderStatusDetails from '../OrderStatusDetails';
import CallRiderModalButton from '../CallRiderModalButton';
import OrderShareModalButtonContainer from '../../containers/OrderShareModalButton';
import DirectionHelpActionModalContainer from '../../containers/DirectionHelpActionModal';

import i18n from '../../utils/i18n';
import bagDeliveroo from './images/bag-deliveroo.svg';
import styles from './styles.scss';

export default function OrderDetails({
  id,
  deliveryAddress,
  deliveryNote,
  orderDetails,
  restaurantName,
  totalAmount,
  imageUrl,
  totals,
  placedAtMessage,
  riderName,
  riderSubtitle,
  contactDetails,
  isSharedOrder,
  shouldShowInfoBanner,
  shouldShowPunchCard,
  orderConfirmationReference,
  directionHelpAction,
  orderHelpDynamicActionsCallRiderWebOn,
  openOrderHelpModal,
  openOrderHelpModalToCallRiderPage,
}) {
  const buttonText = contactDetails && contactDetails.text;
  const contactModal = contactDetails && contactDetails.modal;
  const showContactButton = contactDetails && contactDetails.showContactButton && riderName;
  const canContact = contactDetails && contactDetails.canContact;
  const canContactActionId = canContact ? 'contact_rider' : 'contact_rider_unavailable';

  return (
    <div className={styles['order-details']}>
      <OrderStatusDetails>
        {!isSharedOrder && (
          <div key="order-buttons" hideonmobile="true">
            <div className={styles.button}>
              <Button isBlock icon={QuestionCircle} type="primary" onClick={openOrderHelpModal}>
                {i18n.gettext('order_status_page.order_help')}
              </Button>
            </div>
            <OrderShareModalButtonContainer />
          </div>
        )}

        {riderName && (
          <div key="rider-details" className={styles['rider-details']}>
            <div className={styles['details-left']}>
              {/* Icon has come from svg as icons are being moved atm */}
              <img alt="bag-deliveroo" src={bagDeliveroo} />
            </div>
            <div className={styles['details-right']}>
              <P>{riderName}</P>
              <P color="grey" size="small">
                {riderSubtitle}
              </P>
            </div>

            {showContactButton &&
              contactModal &&
              !orderHelpDynamicActionsCallRiderWebOn &&
              !isSharedOrder && (
                <div className={styles['rider-details-call-link-container']}>
                  <CallRiderModalButton
                    riderNumber={contactModal.riderNumber}
                    modalText={contactModal.text}
                    modalHeader={contactModal.header}
                    appElement="#__next"
                  >
                    {/* eslint-disable-next-line react/button-has-type */}
                    <button className={styles['rider-details-call-link']}>{buttonText}</button>
                  </CallRiderModalButton>
                </div>
              )}

            {showContactButton &&
              orderHelpDynamicActionsCallRiderWebOn &&
              !isSharedOrder && (
                <div
                  className={classnames(
                    styles['rider-details-call-link-container'],
                    styles['rider-details-call-link-mobile'],
                  )}
                >
                  <a
                    className={styles['rider-details-call-link']}
                    href={`/orders/${id}/help?redirectTo=/orders/${id}/status&daai=order_help&daat=action_to_action&dasai=${canContactActionId}&dasat=call_number`}
                    aria-label={i18n.gettext('order_status_page.order_help')}
                    title={i18n.gettext('order_status_page.order_help')}
                  >
                    {buttonText}
                  </a>
                </div>
              )}

            {showContactButton &&
              orderHelpDynamicActionsCallRiderWebOn &&
              !isSharedOrder && (
                <div
                  className={classnames(
                    styles['rider-details-call-link-container'],
                    styles['rider-details-call-link-desktop'],
                  )}
                >
                  <UnstyledButton
                    onClick={() => openOrderHelpModalToCallRiderPage(canContactActionId)}
                  >
                    <P color="teal">{buttonText}</P>
                  </UnstyledButton>
                </div>
              )}
          </div>
        )}

        {shouldShowInfoBanner && (
          <div className={styles['info-banner']} nocard="true">
            <InfoBannerContainer />
          </div>
        )}

        {shouldShowPunchCard && (
          <div className={styles['punch-card']} nocard="true">
            <PunchCardContainer />
          </div>
        )}

        {deliveryAddress && (
          <div key="delivery-details" className={styles['delivery-details']}>
            <div className={styles['details-left']}>
              <Span color="grey">
                <Icon component={Pin} />
              </Span>
            </div>
            <div className={styles['details-right']}>
              <P>{deliveryAddress}</P>
              {deliveryNote &&
                !isSharedOrder && (
                  <P color="grey" size="small">
                    &quot;
                    {deliveryNote}
                    &quot;
                  </P>
                )}
            </div>
          </div>
        )}

        {!isSharedOrder &&
          (directionHelpAction || orderConfirmationReference) && (
            <Fragment>
              {directionHelpAction && (
                <div className={styles['order-details-row']} key="direction-help-action">
                  <Row
                    secondary={directionHelpAction.address}
                    left={<Icon component={Pin} color="grey" />}
                    right={<DirectionHelpActionModalContainer />}
                  >
                    {directionHelpAction.restaurantName}
                  </Row>
                </div>
              )}

              {orderConfirmationReference && (
                <div className={styles['order-details-row']} key="order-confirmation-reference">
                  <Row
                    secondary={orderConfirmationReference.subtitle}
                    left={<Icon component={BagDeliveroo} color="grey" />}
                  >
                    {orderConfirmationReference.title}
                  </Row>
                </div>
              )}
            </Fragment>
          )}

        <OrderItemsSummary
          id={id}
          orderDetails={orderDetails}
          restaurantName={restaurantName}
          imageUrl={imageUrl}
          totalAmount={totalAmount}
          totals={totals}
          placedAtMessage={placedAtMessage}
          key="order-summary-details"
        />
      </OrderStatusDetails>
    </div>
  );
}

OrderDetails.propTypes = {
  id: PropTypes.string.isRequired,
  deliveryAddress: PropTypes.string,
  deliveryNote: PropTypes.string,
  orderDetails: PropTypes.shape({
    orderItems: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        quantity: PropTypes.number,
        totalPrice: PropTypes.string,
        modifiersDescription: PropTypes.string,
      }),
    ),
    orderNumber: PropTypes.string,
  }).isRequired,
  restaurantName: PropTypes.string.isRequired,
  totalAmount: PropTypes.string,
  imageUrl: PropTypes.string.isRequired,
  totals: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  placedAtMessage: PropTypes.string,
  riderName: PropTypes.string,
  riderSubtitle: PropTypes.string,
  contactDetails: PropTypes.shape({
    showContactButton: PropTypes.bool,
    canContact: PropTypes.bool,
    text: PropTypes.string,
  }),
  isSharedOrder: PropTypes.bool,
  /**
   * Whether there's a punchCard showing
   */
  shouldShowInfoBanner: PropTypes.bool,
  /**
   * Whether to show the punch card
   */
  shouldShowPunchCard: PropTypes.bool,
  /**
   * Details for the order confirmation reference which is a modal with the order ID
   * to show to collect the order
   */
  orderConfirmationReference: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
  }),
  /**
   * Details for the direction help which is a modal with links for directions to the restaurant
   * to go to collect the order
   */
  directionHelpAction: PropTypes.shape({
    restaurantName: PropTypes.string.isRequired,
    address: PropTypes.string.isRequired,
  }),
  orderHelpDynamicActionsCallRiderWebOn: PropTypes.bool,
  /**
   * Action to open the OrderHelpModal
   */
  openOrderHelpModal: PropTypes.func,
  /**
   * Opens the Order Help straight to the call rider page
   */
  openOrderHelpModalToCallRiderPage: PropTypes.func,
};

OrderDetails.defaultProps = {
  placedAtMessage: null,
  deliveryAddress: null,
  deliveryNote: null,
  totalAmount: null,
  totals: [],
  riderName: null,
  riderSubtitle: null,
  contactDetails: {},
  isSharedOrder: false,
  shouldShowInfoBanner: true,
  shouldShowPunchCard: false,
  orderConfirmationReference: null,
  directionHelpAction: null,
  orderHelpDynamicActionsCallRiderWebOn: false,
  openOrderHelpModal: () => {},
  openOrderHelpModalToCallRiderPage: () => {},
};
